import React, { useState, useEffect } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import { FiUser, FiRotateCw, FiShoppingCart, FiPhone } from "react-icons/fi";
import { toast } from "react-toastify";
import {
  signInWithPopup,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { auth, provider } from "./utils/firebaseConfig";
import "react-toastify/dist/ReactToastify.css";
import { Toaster } from "react-hot-toast";
import { useLocation } from "react-router-dom";

import Inicio from "./pages/Inicio";
import ProductDetail from "./pages/ProductDetail";
import QuienesSomos from "./pages/QuienesSomos";
import RepetirPedido from "./pages/RepetirPedido";
import LoginRequired from "./pages/LoginRequired";
import Pedidos from "./pages/Pedidos";
import SubirCSV from "./components/SubirCSV";
import BotonCarrito from "./components/BotonCarrito";
import Footer from "./components/Footer";
import ModalLogin from "./components/ModalLogin";
import WhatsAppButton from "./components/WhatsAppButton";
import Success from "./pages/Success";
import Cotizar from "./pages/Cotizar";
import Catalogo from "./pages/Catalogo";

function App() {
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [user, setUser] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [mostrarPopupContacto, setMostrarPopupContacto] = useState(false);
  const [animandoLogo, setAnimandoLogo] = useState(false);
  const [animandoFavicon, setAnimandoFavicon] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const location = useLocation();
  const rutasConScroll = ["/catalogo", "/productdetail", "/cotizar"];
  const esRutaConScroll = rutasConScroll.some(r => location.pathname.toLowerCase().startsWith(r));
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0); 
  const normalize = (str) =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const categoriasDisponibles = [
    "cafeteria",
    "desechables",
    "limpieza",
    "proteccion personal",
    "seguridad industrial",
    "insumos descartables",
    "linea institucional",
    "kimberly clark",
    "Hogar",
  ];

  useEffect(() => {
    // Solo mostrar la animación una vez por sesión
    const yaMostro = sessionStorage.getItem("faviconMostrado");
  
    if (!yaMostro) {
      setAnimandoFavicon(true);
      const timeout = setTimeout(() => {
        setAnimandoFavicon(false);
        sessionStorage.setItem("faviconMostrado", "1");
      }, 1500);
  
      return () => clearTimeout(timeout);
    } else {
      setAnimandoFavicon(false);
    }
  }, []);
  
  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch(console.error);
  }, []);
  
  useEffect(() => {
    setLastScrollY(0);
  }, [location.pathname]);
  
  useEffect(() => {
    if (!esRutaConScroll) {
      setShowNavbar(true); // Siempre mostrar navbar fuera de las rutas
      return;
    }
  
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
  
      if (currentScrollY <= 0) {
        setShowNavbar(true);
      } else if (currentScrollY > lastScrollY) {
        setShowNavbar(false);
      } else {
        setShowNavbar(true);
      }
  
      setLastScrollY(currentScrollY);
    };
  
    window.addEventListener("scroll", handleScroll);
  
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [lastScrollY, esRutaConScroll, location.pathname]);

  useEffect(() => {
    const timeout = setTimeout(() => setAnimandoFavicon(false), 1500);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, setUser);
    return () => unsubscribe();
  }, []);

  const loginWithGoogle = async () => {
    try {
      toast.info("Iniciando sesión con Google...");
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      toast.success("Sesión iniciada correctamente");
    } catch (e) {
      toast.error("❌ Error al iniciar sesión");
      console.error("Google Login Error:", e);
    }
  };

  const loginWithEmail = async () => {
    if (!email || !password) return toast.warn("Completa todos los campos");
  
    if (email.trim().toLowerCase() !== "admin@empresa.com") {
      return toast.error("❌ Solo el administrador puede iniciar sesión con correo.");
    }
  
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      setUser(result.user);
      toast.success("Sesión iniciada correctamente como administrador");
      setShowEmailLogin(false);
    } catch (e) {
      toast.error("❌ Error al iniciar sesión con correo");
      console.error("Correo Login Error:", e);
    }
  };

  const logout = () => {
    signOut(auth)
      .then(() => {
        setUser(null);
        toast.info("Sesión cerrada");
      })
      .catch((e) => toast.error("Error al cerrar sesión"));
  };

  const handleRepetirPedidoClick = (e) => {
    if (!user || user.isAnonymous) {
      e.preventDefault();
      toast.error("Debes iniciar sesión para acceder a pedidos anteriores");
      setShowEmailLogin(true);
    }
  };

  return (
    <div className="bg-gray-100 min-h-screen scroll-smooth">
      {animandoFavicon && (
        <div className="fixed inset-0 z-[9999] bg-white flex items-center justify-center">
          <img
            src="/favicon.png"
            alt="Logo animado"
            className="w-32 h-32 object-contain animate-spinBounce"
          />
        </div>
      )}

      {/* Barra de navegación con buscador e ítems */}
      <nav
        className={`sticky top-0 z-50 transition-transform duration-300 ${
          esRutaConScroll ? (showNavbar ? 'translate-y-0' : '-translate-y-full') : 'translate-y-0'
        } bg-white/60 backdrop-blur-md shadow-lg px-4 py-3 border-b border-gray-200`}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <div className="flex justify-between items-center w-full md:w-auto">
            <Link to="/">
              <img
                src="https://i.imgur.com/P3vupOA.png"
                alt="Logo de la empresa"
                className={`h-10 w-auto object-contain transition-transform duration-200 ${
                  animandoLogo ? "scale-90" : "scale-100"
                }`}
              />
            </Link>
            <button
              className="md:hidden text-sky-800 focus:outline-none"
              onClick={() => setShowMenu(!showMenu)}
            >
              ☰
            </button>
          </div>

          {/* Buscador */}
          <div className="w-full flex flex-col sm:flex-row sm:items-center gap-2 md:max-w-md">
            <input
              type="text"
              placeholder="Buscar productos..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && busqueda.trim()) {
                  const normalizada = normalize(busqueda.trim());
                  const matchCategoria = categoriasDisponibles.find(
                    (cat) => normalize(cat) === normalizada
                  );
            
                  const params = new URLSearchParams();
                  if (matchCategoria) {
                    params.set("categoria", matchCategoria);
                  } else {
                    params.set("nombre", busqueda.trim());
                  }
                  params.set("scroll", "1");
            
                  window.location.href = `/catalogo?${params.toString()}`;
                }
              }}
              className="flex-grow px-4 py-2 border border-gray-300 rounded-md text-sm"
            />
            
            <button
              onClick={() => {
                if (busqueda.trim()) {
                  const normalizada = normalize(busqueda.trim());
                  const matchCategoria = categoriasDisponibles.find(
                    (cat) => normalize(cat) === normalizada
                  );
            
                  const params = new URLSearchParams();
                  if (matchCategoria) {
                    params.set("categoria", matchCategoria);
                  } else {
                    params.set("nombre", busqueda.trim());
                  }
                  params.set("scroll", "1");
            
                  window.location.href = `/catalogo?${params.toString()}`;
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Buscar
            </button>
          </div>

          <div className="hidden md:flex space-x-6 items-center text-sm">
            <Link
              to="/repetir"
              onClick={handleRepetirPedidoClick}
              className="relative px-4 py-1 text-black font-semibold overflow-hidden
                         before:absolute before:bottom-0 before:left-0 before:h-0.5 before:w-0
                         before:bg-cyan-400 before:transition-all before:duration-300
                         hover:before:w-full"
            >
              Pedir de nuevo
            </Link>

            {user && !user.isAnonymous ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-black">{user.email}</span>
                <button
                  onClick={logout}
                  className="relative px-6 py-2 rounded-xl text-white font-semibold bg-black overflow-hidden shadow-md transition duration-300"
                >
                  <span className="relative z-10 flex items-center gap-1">
                    <FiUser /> Salir
                  </span>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowEmailLogin(true)}
                className="relative px-6 py-2 rounded-xl text-white font-semibold bg-empresarial overflow-hidden shadow-md transition duration-300"
              >
                <span className="relative z-10 flex items-center gap-1">
                  <FiUser /> Iniciar sesión
                </span>
              </button>
            )}
          </div>

          {showMenu && (
            <div className="md:hidden mt-2 space-y-2 text-sm">
              <Link 
                to="/repetir" 
                onClick={handleRepetirPedidoClick}
                className="block text-sky-700"
              >
                Pedir de nuevo
              </Link>
              {user && !user.isAnonymous ? (
                <button onClick={logout} className="block text-sky-700">
                  Salir
                </button>
              ) : (
                <button onClick={() => setShowEmailLogin(true)} className="block text-sky-700">
                  Iniciar sesión
                </button>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* Login modal y rutas */}
      <ModalLogin
        visible={showEmailLogin}
        onClose={() => setShowEmailLogin(false)}
        email={email}
        setEmail={setEmail}
        password={password}
        setPassword={setPassword}
        loginWithEmail={loginWithEmail}
        loginWithGoogle={loginWithGoogle}
      />

      <Routes>
        <Route path="/" element={<Inicio user={user} />} />
        <Route path="/producto/:sku" element={<ProductDetail user={user} />} />
        <Route path="/quienes" element={<QuienesSomos />} />
        <Route path="/repetir" element={user && !user.isAnonymous ? <RepetirPedido /> : <Navigate to="/login-required" />} />
        <Route path="/login-required" element={<LoginRequired />} />
        <Route path="/pedidos" element={<Pedidos user={user} />} />
        <Route path="/success" element={<Success />} />
        <Route path="/cotizar" element={<Cotizar />} />
        <Route path="/catalogo" element={<Catalogo />} />
        <Route path="/subircsv" element={<SubirCSV />} />
      </Routes>

      <Toaster position="bottom-center" />
      <Footer setMostrarPopupContacto={setMostrarPopupContacto} />

      {mostrarPopupContacto && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/30 text-gray-800 rounded-xl border-2 border-cyan-400 p-6 max-w-sm text-center relative backdrop-blur-md shadow-xl">
            <h2 className="text-xl font-semibold mb-4">¿Deseas contactarnos por WhatsApp?</h2>
            <div className="flex justify-center gap-4">
              <a
                href="https://wa.me/50255106042"
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white transition shadow-md"
              >
                Ir a WhatsApp
              </a>
              <button
                onClick={() => setMostrarPopupContacto(false)}
                className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400 text-gray-800 transition"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Botones flotantes */}
      <div className="fixed bottom-24 right-4 z-50 group">
        <a
          href="https://wa.me/50255106042?text=Hola, necesito más información."
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition duration-300 animate-floating"
        >
          <FiPhone className="w-5 h-5" />
        </a>
        <span className="absolute top-1/2 right-14 -translate-y-1/2 bg-black text-white text-xs px-3 py-1 rounded shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap">
          Contactar por WhatsApp
        </span>
      </div>

      <div className="fixed bottom-4 right-4 z-50 animate-floating">
        <BotonCarrito />
      </div>
    </div>
  );
}

export default App;


